/** @flow */

import { takeLatest, put, all, call, select } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";

import { downloadCsvFile } from "@sagas/utils";
import * as selectors from "@modules/reporting/selectors/reports/absence-monitoring";
import {
  GetReportingAbsenceMonitoringDefaultReports,
  GetReportingAbsenceMonitoringMyReports,
  GetReportingAbsenceMonitoringReport,
  CreateAbsenceMonitoringReport,
  UpdateAbsenceMonitoringReport,
  DeleteAbsenceMonitoringReport,
  GetAbsenceMonitoringFiltersSchema,
  ExportAbsenceMonitoringReport,
  ExportAbsenceMonitoringReportSuccess,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ReportRequest = $Exact<{
  clientId: number,
}>;

function* onGetAbsenceMonitoringFiltersSchema({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("AbsenceMonitoringDetailsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceMonitoringFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringFiltersSchemaError(e.response));
  }
}

function* onGetReportingAbsenceMonitoringDefaultReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/default-reports`,
    );

    yield put(actionCreators.GetReportingAbsenceMonitoringDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingAbsenceMonitoringDefaultReportsError(e.response));
  }
}

function* onGetReportingAbsenceMonitoringMyReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/my-saved-reports`,
    );

    yield put(actionCreators.GetReportingAbsenceMonitoringMyReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingAbsenceMonitoringMyReportsError(e.response));
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: Object,
  noRedirect?: boolean,
  page?: number,
}>;

function* onGetReportingAbsenceMonitoringReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error("Invalid payload provided");
    }

    const pagination = yield select(selectors.getAbsenceMonitoringDetailsPagination);
    const currentPage = pagination.page;
    const page = payload.page ? payload.page : currentPage;

    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring`,
        payload.report.filters,
        {
          params: {
            page,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/charts`,
        payload.report.filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingAbsenceMonitoringReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingAbsenceMonitoringChartsSuccess(chartsResponse.data)),
    ]);

    if (!payload.noRedirect) {
      yield put(
        push(route("REPORTING_ABSENCE_MONITORING_DETAILS_REPORT", { clientId: payload.clientId })),
      );
    }
  } catch (e) {
    yield put(actionCreators.GetReportingAbsenceMonitoringReportError(e.response));
  }
}

type CreateAbsenceMonitoringReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

type DeleteAbsenceMonitoringReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

type UpdateAbsenceMonitoringReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onCreateAbsenceMonitoringReport({
  payload,
}: Action<CreateAbsenceMonitoringReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateAbsenceMonitoringReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAbsenceMonitoringMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAbsenceMonitoringReportError(e.response));
  }
}

export function* onUpdateAbsenceMonitoringReport({
  payload,
}: Action<UpdateAbsenceMonitoringReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingAbsenceMonitoringReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateAbsenceMonitoringReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAbsenceMonitoringMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceMonitoringReportError(e.response));
  }
}

export function* onDeleteAbsenceMonitoringReport({
  payload,
}: Action<DeleteAbsenceMonitoringReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteAbsenceMonitoringReportSuccess()),
      put(actionCreators.GetReportingAbsenceMonitoringMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAbsenceMonitoringReportError(e.response));
  }
}

type ExportAbsenceAbsenceHistoryRequest = $Exact<{
  clientId: number,
  filters: Object,
}>;

export function* onExportAbsenceMonitoringReport({
  payload,
}: Action<ExportAbsenceAbsenceHistoryRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/export`,
      payload.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportAbsenceMonitoringReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceMonitoringReportError(e.response));
  }
}

export function* onExportAbsenceMonitoringReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "absence-monitoring-report.csv");
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(
      GetReportingAbsenceMonitoringDefaultReports,
      onGetReportingAbsenceMonitoringDefaultReports,
    ),
    takeLatest(GetReportingAbsenceMonitoringMyReports, onGetReportingAbsenceMonitoringMyReports),
    takeLatest(GetReportingAbsenceMonitoringReport, onGetReportingAbsenceMonitoringReport),
    takeLatest(CreateAbsenceMonitoringReport, onCreateAbsenceMonitoringReport),
    takeLatest(UpdateAbsenceMonitoringReport, onUpdateAbsenceMonitoringReport),
    takeLatest(DeleteAbsenceMonitoringReport, onDeleteAbsenceMonitoringReport),
    takeLatest(GetAbsenceMonitoringFiltersSchema, onGetAbsenceMonitoringFiltersSchema),
    takeLatest(ExportAbsenceMonitoringReport, onExportAbsenceMonitoringReport),
    takeLatest(ExportAbsenceMonitoringReportSuccess, onExportAbsenceMonitoringReportSuccess),
  ]);
}
